// Plan prices and upgrades (docs/MVP.md → Plans and Subscriptions). Pure functions.
// Prices are set by the platform per plan. A months plan's Plan.priceKurus is the total for the
// whole duration; a season plan's is the price of one week (product decision 2026-10-11), and a
// purchase pays it for every week left until the season ends.

import { addDays, istanbulDate, istanbulToUtc } from "../time";

/**
 * A season ends when its exam day is over: midnight after `examDate` (YYYY-MM-DD) in Istanbul.
 * Staff enter the exam day in office.
 */
export function seasonEndsAt(examDate: string): Date {
  return istanbulToUtc(addDays(examDate, 1), 0);
}

/** The exam day (YYYY-MM-DD) of a season that ends at `endsAt`. */
export function seasonExamDate(endsAt: Date): string {
  return addDays(istanbulDate(endsAt), -1);
}

/** The season of a season plan: "2027 Dönemi", ending on the exam. */
export type PlanSeasonInfo = { id: string; name: string; endsAt: Date };

/** How long a plan runs: a number of months, or until its season ends. Exactly one is set. */
export type PlanDuration = { durationMonths: number | null; season: PlanSeasonInfo | null };

export type PlanPrice = PlanDuration & { weeklyHours: number; priceKurus: number };

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Whole weeks a season plan bought at `now` pays for: every started week until the end. */
export function seasonWeeksLeft(endsAt: Date, now: Date): number {
  return Math.max(Math.ceil((endsAt.getTime() - now.getTime()) / WEEK_MS), 0);
}

/**
 * A season plan is sold while at least a full week is left (product decision 2026-10-11);
 * staff can take it off sale earlier in office.
 */
export function isSeasonOnSale(endsAt: Date, now: Date): boolean {
  return endsAt.getTime() - now.getTime() >= WEEK_MS;
}

/** What a season plan costs when bought at `now`: its weekly price for every week left. */
export function seasonPriceKurus(weeklyKurus: number, endsAt: Date, now: Date): number {
  return weeklyKurus * seasonWeeksLeft(endsAt, now);
}

/**
 * A plan's list price for a period: the months plan's price, or the season plan's weekly price
 * for the weeks of the period (to compare plans within one subscription period).
 */
export function listPriceForPeriod(plan: PlanPrice, periodStart: Date, periodEnd: Date): number {
  if (plan.season) return plan.priceKurus * seasonWeeksLeft(periodEnd, periodStart);
  return plan.priceKurus;
}

/** Price per month, shown as "4.000 ₺ /ay" (Figma Desktop - 4, 60). `priceKurus` is the total. */
export function monthlyPriceKurus(
  plan: { priceKurus: number } & PlanDuration,
  now = new Date(),
): number {
  if (plan.durationMonths) return Math.round(plan.priceKurus / plan.durationMonths);
  const weeks = plan.season ? seasonWeeksLeft(plan.season.endsAt, now) : 0;
  // Whole lira: a monthly equivalent, not a price anyone pays.
  return weeks > 0
    ? Math.round(((plan.priceKurus / weeks) * (52 / 12)) / 100) * 100
    : plan.priceKurus;
}

/**
 * What a subscription pays for its own plan (product decision 2026-10-10): the price locked
 * when it was bought or last changed, unless today's list price is lower. A price rise reaches
 * only new purchases; a price cut reaches everyone. A plan change takes the new plan's price.
 */
export function subscriptionPriceKurus(lockedKurus: number, listKurus: number): number {
  return Math.min(lockedKurus, listKurus);
}

/** Two plans run for the same time: the same number of months, or the same season. */
export function sameDuration(a: PlanDuration, b: PlanDuration): boolean {
  if (a.season || b.season) return a.season?.id === b.season?.id;
  return a.durationMonths === b.durationMonths;
}

/** Upgrades raise the weekly hours within the same duration; other changes apply at renewal. */
export function isUpgrade(current: PlanPrice, target: PlanPrice): boolean {
  return sameDuration(current, target) && target.weeklyHours > current.weeklyHours;
}

/**
 * Upgrade price (product decision): the price difference for the remaining part of the
 * current period. The period end does not change. Rounded up to whole lira. Both prices are
 * for the whole period (`listPriceForPeriod` for a season plan).
 */
export function upgradeCostKurus(input: {
  current: PlanPrice;
  target: PlanPrice;
  periodStart: Date;
  periodEnd: Date;
  now: Date;
}): number {
  const total = input.periodEnd.getTime() - input.periodStart.getTime();
  const remaining = Math.min(Math.max(input.periodEnd.getTime() - input.now.getTime(), 0), total);
  const difference = Math.max(input.target.priceKurus - input.current.priceKurus, 0);
  if (total <= 0 || remaining === 0 || difference === 0) return 0;
  return Math.ceil((difference * remaining) / total / 100) * 100;
}

/** End of a period that starts at `start` and lasts `months` calendar months. */
export function periodEnd(start: Date, months: number): Date {
  const end = new Date(start);
  end.setUTCMonth(end.getUTCMonth() + months);
  return end;
}

/** End of a plan's period that starts at `start`: months later, or the season's end. */
export function planPeriodEnd(plan: PlanDuration, start: Date): Date {
  if (plan.season) return plan.season.endsAt;
  return periodEnd(start, plan.durationMonths ?? 1);
}

const lira = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 });

/** "4.000" for 400000 kuruş (the ₺ sign is shown separately, as in Figma). */
export function formatLira(kurus: number): string {
  return lira.format(kurus / 100);
}

export function durationLabel(months: number): string {
  return months === 12 ? "Yıllık" : `${months} Ay`;
}

/** "1 Ay", "3 Ay" or the season's name ("2027 Dönemi"). */
export function planDurationLabel(plan: {
  durationMonths: number | null;
  season: { name: string } | null;
}): string {
  if (plan.season) return plan.season.name;
  return durationLabel(plan.durationMonths ?? 1);
}
